import {FC} from 'react'
import {UseFormReturn} from 'react-hook-form'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useTranslation} from '@/translations/i18n'
import {MailerFormValidationSchema} from '@/features/mailer/types'
import {Label} from '@/components/ui/label/Label'
import {ComboboxProvider, SelectProvider} from '@ariakit/react'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectTrigger,
    SelectVirtualList
} from '@/components/ui/select-atoms/SelectAtoms'
import {useGetUserSpecializations} from '@/features/users/queries/useGetUserSpecializations'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {MailerCollapsibleSection} from '@/features/mailer/components/mailer-collapsible-section/MailerCollapsibleSection'

interface ConsultantFiltersPanelProps {
    form: UseFormReturn<MailerFormValidationSchema>
}

const STATUS_OPTIONS = [
    {value: 'operative', label: 'mailer:status_operative'},
    {value: 'candidate', label: 'mailer:status_candidate'},
    {value: 'suspended', label: 'mailer:status_suspended'},
    {value: 'absent', label: 'mailer:status_absent'},
    {value: 'terminated', label: 'mailer:status_terminated'}
] as const

const CONTRACT_TYPE_OPTIONS = [
    {value: 'consultant', label: 'mailer:contract_type_consultant'},
    {value: 'flat_rate', label: 'mailer:contract_type_flat_rate'},
    {value: 'employee', label: 'mailer:contract_type_employee'}
] as const

const WORK_SECTOR_OPTIONS = [
    {value: 'domiciliary', label: 'mailer:work_sector_domiciliary'},
    {value: 'outpatient', label: 'mailer:work_sector_outpatient'},
    {value: 'outpatient_domiciliary', label: 'mailer:work_sector_outpatient_domiciliary'}
] as const

export const ConsultantFiltersPanel: FC<ConsultantFiltersPanelProps> = ({form}) => {
    const {t} = useTranslation()
    const {setValue, watch} = form
    const specializationId = watch('consultantSpecializationId')
    const status = watch('consultantStatus')
    const contractType = watch('consultantContractType')
    const workSector = watch('consultantWorkSector')

    const specializationsQuery = useGetUserSpecializations({limit: 100})

    const activeCount = [
        watch('consultantSpecializationId'),
        watch('consultantStatus'),
        watch('consultantContractType'),
        watch('consultantWorkSector')
    ].filter(Boolean).length

    return (
        <MailerCollapsibleSection
            title={t('mailer:consultant_filters_title')}
            activeCount={activeCount}
            activeCountLabel={t('mailer:active_filters_count', {count: activeCount})}
        >
            <Flexbox gap={4} fullWidth style={{flexWrap: 'wrap'}}>
                {/* Specialization */}
                <Flexbox direction="column" gap={1.5} style={{flex: '1 1 250px'}}>
                    <ComboboxProvider setValue={specializationsQuery.onSearch} resetValueOnHide>
                        <SelectProvider
                            value={specializationId ?? ''}
                            setValue={value => {
                                setValue('consultantSpecializationId', value === '' ? undefined : value)
                            }}
                        >
                            <Flexbox direction="column" gap={1.5} fullWidth>
                                <Label>
                                    {t('mailer:specialization')}{' '}
                                    <span className="description">({t('commons:optional')})</span>
                                </Label>
                                <SelectTrigger
                                    placeholder={t('commons:select')}
                                    fullWidth
                                    value={specializationId}
                                    displayValue={() => {
                                        const match = specializationsQuery.remappedData.find(
                                            s => s.id === specializationId
                                        )
                                        return match?.name ?? ''
                                    }}
                                />
                                <SelectComboboxPopover sameWidth>
                                    <SelectComboboxInput placeholder={t('commons:search_placeholder')} />
                                    <SelectComboboxList>
                                        {specializationsQuery.isLoading ? (
                                            <>
                                                {Array.from({length: 6}, (_, index) => (
                                                    <Skeleton key={index} height={32} />
                                                ))}
                                            </>
                                        ) : specializationsQuery.isError ? (
                                            <InputHelpText error={t('errors:default')} />
                                        ) : specializationsQuery.remappedData.length > 0 ? (
                                            <>
                                                <SelectItem value="">-</SelectItem>
                                                <SelectVirtualList
                                                    data={specializationsQuery.remappedData}
                                                    totalCount={specializationsQuery.remappedData.length}
                                                    overscan={100}
                                                    endReached={specializationsQuery.fetchNextPage}
                                                    itemContent={(_, item) => (
                                                        <SelectItem key={item.id} value={item.id}>
                                                            {item.name}
                                                        </SelectItem>
                                                    )}
                                                />
                                            </>
                                        ) : (
                                            <InputHelpText helpText={t('commons:no_results')} />
                                        )}
                                    </SelectComboboxList>
                                </SelectComboboxPopover>
                            </Flexbox>
                        </SelectProvider>
                    </ComboboxProvider>
                </Flexbox>

                {/* Contract Type */}
                <Flexbox direction="column" gap={1.5} style={{flex: '1 1 200px'}}>
                    <ComboboxProvider>
                        <SelectProvider
                            value={contractType ?? ''}
                            setValue={value => {
                                setValue(
                                    'consultantContractType',
                                    value === ''
                                        ? undefined
                                        : (value as MailerFormValidationSchema['consultantContractType'])
                                )
                            }}
                        >
                            <Flexbox direction="column" gap={1.5} fullWidth>
                                <Label>
                                    {t('mailer:contract_type')}{' '}
                                    <span className="description">({t('commons:optional')})</span>
                                </Label>
                                <SelectTrigger
                                    placeholder={t('commons:select')}
                                    fullWidth
                                    value={contractType}
                                    displayValue={() => {
                                        const option = CONTRACT_TYPE_OPTIONS.find(o => o.value === contractType)
                                        return option ? t(option.label) : ''
                                    }}
                                />
                                <SelectComboboxPopover sameWidth>
                                    <SelectComboboxList>
                                        <SelectItem value="">-</SelectItem>
                                        {CONTRACT_TYPE_OPTIONS.map(option => (
                                            <SelectItem key={option.value} value={option.value}>
                                                {t(option.label)}
                                            </SelectItem>
                                        ))}
                                    </SelectComboboxList>
                                </SelectComboboxPopover>
                            </Flexbox>
                        </SelectProvider>
                    </ComboboxProvider>
                </Flexbox>

                {/* Work Sector */}
                <Flexbox direction="column" gap={1.5} style={{flex: '1 1 200px'}}>
                    <ComboboxProvider>
                        <SelectProvider
                            value={workSector ?? ''}
                            setValue={value => {
                                setValue(
                                    'consultantWorkSector',
                                    value === ''
                                        ? undefined
                                        : (value as MailerFormValidationSchema['consultantWorkSector'])
                                )
                            }}
                        >
                            <Flexbox direction="column" gap={1.5} fullWidth>
                                <Label>
                                    {t('mailer:work_sector')}{' '}
                                    <span className="description">({t('commons:optional')})</span>
                                </Label>
                                <SelectTrigger
                                    placeholder={t('commons:select')}
                                    fullWidth
                                    value={workSector}
                                    displayValue={() => {
                                        const option = WORK_SECTOR_OPTIONS.find(o => o.value === workSector)
                                        return option ? t(option.label) : ''
                                    }}
                                />
                                <SelectComboboxPopover sameWidth>
                                    <SelectComboboxList>
                                        <SelectItem value="">-</SelectItem>
                                        {WORK_SECTOR_OPTIONS.map(option => (
                                            <SelectItem key={option.value} value={option.value}>
                                                {t(option.label)}
                                            </SelectItem>
                                        ))}
                                    </SelectComboboxList>
                                </SelectComboboxPopover>
                            </Flexbox>
                        </SelectProvider>
                    </ComboboxProvider>
                </Flexbox>

                {/* Status */}
                <Flexbox direction="column" gap={1.5} style={{flex: '1 1 200px'}}>
                    <ComboboxProvider>
                        <SelectProvider
                            value={status ?? ''}
                            setValue={value => {
                                setValue(
                                    'consultantStatus',
                                    value === '' ? undefined : (value as MailerFormValidationSchema['consultantStatus'])
                                )
                            }}
                        >
                            <Flexbox direction="column" gap={1.5} fullWidth>
                                <Label>
                                    {t('mailer:status')} <span className="description">({t('commons:optional')})</span>
                                </Label>
                                <SelectTrigger
                                    placeholder={t('commons:select')}
                                    fullWidth
                                    value={status}
                                    displayValue={() => {
                                        const option = STATUS_OPTIONS.find(o => o.value === status)
                                        return option ? t(option.label) : ''
                                    }}
                                />
                                <SelectComboboxPopover sameWidth>
                                    <SelectComboboxList>
                                        <SelectItem value="">-</SelectItem>
                                        {STATUS_OPTIONS.map(option => (
                                            <SelectItem key={option.value} value={option.value}>
                                                {t(option.label)}
                                            </SelectItem>
                                        ))}
                                    </SelectComboboxList>
                                </SelectComboboxPopover>
                            </Flexbox>
                        </SelectProvider>
                    </ComboboxProvider>
                </Flexbox>
            </Flexbox>
        </MailerCollapsibleSection>
    )
}
